// @flow

import React from 'react';
import { withRouter } from 'react-router';
import type { RouterHistory, Location } from 'react-router';
import styled from 'styled-components';
import type { SignUpBlockData } from 'api/cms';
import type { PublicPage } from 'api/website';
import type { CurrentUser } from 'api/currentUser';
import { trackEvent } from 'utils/analytics';
import { hiDPI } from 'utils/style';
import { isExternalURL } from 'utils/url';
import Box from 'components/Box';
import { RichText } from 'components/Text';
import { getSrc } from 'components/Image';
import { gutterWidth, textStyles, breakpoints, maxWidth } from 'theme';
import EmailSignupFormContainer from 'containers/EmailSignupFormContainer';
import useTemporaryStorage from '../../utils/useTemporaryStorage';
import { getParam } from '../../utils/url';

const { useEffect, useState } = React;

const Container = styled(({ imageHandle, ...props }) => <Box {...props} />)`
  background-size: cover;
  background-position: center;
  ${({ imageHandle, mimetype }) => `
    background-image: url('${getSrc({
      handle: imageHandle,
      mimetype,
      width: 800
    })}');
    ${hiDPI()}: {
      background-image: url('${getSrc({
        handle: imageHandle,
        mimetype,
        width: 1600
      })}');
    }
    @media (min-width: ${breakpoints[3]}) {
      background-image: url('${getSrc({
        handle: imageHandle,
        mimetype,
        width: 1280
      })}');
      ${hiDPI()}: {
        background-image: url('${getSrc({
          handle: imageHandle,
          mimetype,
          width: 2560
        })}');
      }
    }
  `}
`;

const Headline = styled(
  ({
    text,
    isPageHeader,
    ...props
  }: {
    text: string,
    isPageHeader: boolean
  }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`
      }}
      {...props}
    />
  )
)`
  && {
    h1,
    h2 {
      ${textStyles.h2};
    }
  }
`;

const SignUpBlock = ({
  data: { headline, redirectUrl, bgImage },
  isPageHeader,
  currentUser,
  history,
  location
}: {
  data: SignUpBlockData,
  isPageHeader: boolean,
  currentUser: CurrentUser,
  content: PublicPage,
  history: RouterHistory,
  location: Location
}) => {
  const tmpId = getParam(location, 'tmpId');
  const [saveTemporaryStorage, temporaryStorage] = useTemporaryStorage<{
    redirectUrl: string
  }>(currentUser.id && currentUser.emailVerified ? tmpId : '');

  const [redirectTo, setRedirectTo] = useState();

  useEffect(
    () => {
      if (
        temporaryStorage &&
        typeof temporaryStorage.redirectUrl === 'string'
      ) {
        if (isExternalURL(temporaryStorage.redirectUrl)) {
          window.location.href = temporaryStorage.redirectUrl;
        } else {
          history.push(temporaryStorage.redirectUrl);
        }
      }
    },
    [history, temporaryStorage]
  );

  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);

  useEffect(
    () => {
      if (!redirectTo && !tmpId) {
        saveTemporaryStorage({
          SIGNUP_BLOCK_REDIRECT: { redirectUrl }
        }).then(id =>
          setRedirectTo(
            `${window.location.pathname}${window.location.search}${
              window.location.search ? '&' : '?'
            }tmpId=${id}`
          )
        );
      }
    },
    [redirectTo, redirectUrl, saveTemporaryStorage, tmpId]
  );

  return (
    <Container data-qa-id="sign-up">
      <Container
        imageHandle={bgImage ? bgImage.handle : null}
        mimetype={bgImage ? bgImage.mimetype : null}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box display="flex" justifyContent="flex-end">
            <Box
              width={[1, null, 0.5]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              <Headline text={headline} isPageHeader={isPageHeader} />

              <Box mt="32px">
                <EmailSignupFormContainer
                  isModal={false}
                  redirectTo={redirectTo}
                  savedListing={null}
                  subscribedSubsite={null}
                  onLogin={null}
                  onSignup={null}
                  signupEmailParams={null}
                  compactView
                  hideLoginBlock
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default withRouter(SignUpBlock);
